import { Avatar, Box, Card, Chip, Divider, Grid, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useMemo, type ComponentType } from 'react';

import {
  Alert as AlertIcon,
  ArrowRight,
  Calendar,
  CheckCircle,
  ClockOutline,
  InformationOutline,
} from '@opengov/react-capital-assets';

import { MetricCard } from '../components/shared/MetricCard';
import { DenseRow, IconBadge, type RowTone } from '../components/vcm/DenseRow';
import { SectionCard } from '../components/vcm/SectionCard';
import { StackedStatusBar } from '../components/vcm/StackedStatusBar';
import { StatusBlock } from '../components/vcm/StatusBlock';
import { TierBadge } from '../components/vcm/TierBadge';
import { TierBar } from '../components/vcm/TierBar';
import { useLanguage } from '../contexts/LanguageContext';
import { useVcm } from '../contexts/VcmContext';
import { useAppNavigation } from '../hooks/useAppNavigation';
import {
  mockGetCaseMetrics,
  mockGetStatusBreakdown,
  mockGetTierDistribution,
  mockGetRecentActivity,
  mockGetNeedsAttention,
  mockGetAllFollowUps,
  mockGetAllGoals,
  mockGetAllReferrals,
  mockGetCases,
  mockGetOrgWorkloads,
  type StatusBreakdown,
  type TierRow,
  type ActivityItem,
  type AttentionItem,
} from '../mocks/vcm-mock-data';
import type { CaseMetrics, TcrCase } from '../types/vcm';
import { ResourceDashboard } from './ResourceDashboard';

const ATTENTION_ICONS: Record<AttentionItem['severity'], ComponentType> = {
  error: AlertIcon,
  warning: ClockOutline,
  info: InformationOutline,
  success: CheckCircle,
};

const ATTENTION_LINKS: Record<AttentionItem['severity'], string> = {
  error: 'clients/search?type=case&focus=overdue-followups',
  warning: 'clients/search?focus=expiring-housing',
  info: 'clients/search?focus=pending-referrals',
  success: 'clients/search?type=case&status=Review',
};

/** Roles that see the supervisor/admin dashboard sections. */
const SUPERVISOR_ROLES = ['Supervisor', 'Organization Manager', 'Program Manager'];

function startOfToday(): Date {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now;
}

function parseDateOnly(value: string): Date {
  return new Date(`${value}T00:00:00`);
}

function formatDueDate(value: string): string {
  return parseDateOnly(value).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

interface ActionItem {
  id: string;
  caseId: string;
  clientName: string;
  label: string;
  detail: string;
  severity: 'error' | 'warning' | 'info';
  tab: string;
}

/** Build a prioritized action queue from real case data. */
function buildActionQueue(cases: TcrCase[], role: string): ActionItem[] {
  const today = startOfToday();
  const allFollowUps = mockGetAllFollowUps();
  const allGoals = mockGetAllGoals();
  const allReferrals = mockGetAllReferrals();
  const items: ActionItem[] = [];
  const caseMap = new Map(cases.map((c) => [c.caseId, c]));

  // Overdue follow-ups
  for (const fu of allFollowUps) {
    const c = caseMap.get(fu.caseId);
    if (!c || !fu.nextFollowUpDate) continue;
    const dueDate = parseDateOnly(fu.nextFollowUpDate);
    if (dueDate < today) {
      const daysOverdue = Math.floor((today.getTime() - dueDate.getTime()) / 86400000);
      items.push({
        id: `fu-${fu.id}`,
        caseId: c.caseId,
        clientName: c.clientName,
        label: `Overdue follow-up (${daysOverdue}d)`,
        detail: `${fu.followUpType ?? 'Follow-up'} · ${c.tier}`,
        severity: 'error',
        tab: 'notes',
      });
    } else if (dueDate.getTime() === today.getTime()) {
      items.push({
        id: `fu-today-${fu.id}`,
        caseId: c.caseId,
        clientName: c.clientName,
        label: 'Follow-up due today',
        detail: `${fu.followUpType ?? 'Follow-up'} · ${c.tier}`,
        severity: 'warning',
        tab: 'notes',
      });
    }
  }

  // Stalled referrals (requested/referred status)
  for (const ref of allReferrals) {
    const c = caseMap.get(ref.caseId);
    if (!c) continue;
    if (ref.status === 'Requested' || ref.status === 'Referred') {
      items.push({
        id: `ref-${ref.id}`,
        caseId: c.caseId,
        clientName: c.clientName,
        label: `Referral ${ref.status.toLowerCase()}`,
        detail: `${ref.agencyName} · ${ref.serviceName}`,
        severity: 'info',
        tab: 'referrals',
      });
    }
  }

  // Cases in Review (supervisor action needed)
  if (SUPERVISOR_ROLES.includes(role)) {
    for (const c of cases) {
      if (c.status === 'Review') {
        items.push({
          id: `review-${c.caseId}`,
          caseId: c.caseId,
          clientName: c.clientName,
          label: 'Awaiting supervisor review',
          detail: `${c.progression} · ${c.tier}`,
          severity: 'warning',
          tab: 'profile',
        });
      }
    }
  }

  // Stalled goals
  for (const goal of allGoals) {
    const c = caseMap.get(goal.caseId);
    if (!c) continue;
    if (goal.status === 'In Progress' && goal.deadlineDate) {
      const deadline = parseDateOnly(goal.deadlineDate);
      if (deadline < today) {
        items.push({
          id: `goal-${goal.id}`,
          caseId: c.caseId,
          clientName: c.clientName,
          label: 'Recovery goal past deadline',
          detail: `${goal.name} · ${c.tier}`,
          severity: 'warning',
          tab: 'recovery',
        });
      }
    }
  }

  // Sort: errors first, then warnings, then info
  const severityOrder = { error: 0, warning: 1, info: 2 };
  items.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
  return items;
}

/* MOCK DATA — replace with real /v2 API calls when backend is connected */
const metrics: CaseMetrics = mockGetCaseMetrics();
const statusBreakdown: StatusBreakdown[] = mockGetStatusBreakdown();
const tierDistribution: TierRow[] = mockGetTierDistribution();
const recentActivity: ActivityItem[] = mockGetRecentActivity();
const needsAttention: AttentionItem[] = mockGetNeedsAttention();

export function DashboardPage() {
  const { navigateToPath } = useAppNavigation();
  const { moduleLabel, role, currentEvent, currentUser } = useVcm();
  const { t, tv } = useLanguage();

  if (role === 'Resource Management') {
    return <ResourceDashboard />;
  }

  const isSupervisor = SUPERVISOR_ROLES.includes(role);
  const allCases = useMemo(() => mockGetCases(), []);
  const actionQueue = useMemo(() => buildActionQueue(allCases, role), [allCases, role]);

  // Supervisor: workload by case manager
  const caseloadData = useMemo(() => {
    if (!isSupervisor) return [];
    const allFollowUps = mockGetAllFollowUps();
    const today = startOfToday();
    const byManager = new Map<string, { open: number; closed: number; overdue: number; cases: TcrCase[] }>();
    for (const c of allCases) {
      const mgr = c.assignedManager || 'Unassigned';
      const entry = byManager.get(mgr) ?? { open: 0, closed: 0, overdue: 0, cases: [] };
      if (c.status === 'Open' || c.status === 'Pending' || c.status === 'Review') entry.open++;
      else if (c.status === 'Closed') entry.closed++;
      entry.cases.push(c);
      byManager.set(mgr, entry);
    }
    // Count overdue follow-ups per manager
    for (const fu of allFollowUps) {
      if (!fu.nextFollowUpDate) continue;
      if (parseDateOnly(fu.nextFollowUpDate) >= today) continue;
      const c = allCases.find((cs) => cs.caseId === fu.caseId);
      if (!c) continue;
      const mgr = c.assignedManager || 'Unassigned';
      const entry = byManager.get(mgr);
      if (entry) entry.overdue++;
    }
    return Array.from(byManager.entries())
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.open - a.open);
  }, [allCases, isSupervisor]);

  // Org-level workloads (admin/program manager)
  const orgWorkloads = useMemo(() => {
    if (role !== 'Organization Manager' && role !== 'Program Manager') return [];
    return mockGetOrgWorkloads();
  }, [role]);

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* ── Welcome Banner ── */}
      <Card>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ gap: 2, px: 2.5, py: 2, bgcolor: 'background.paper' }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ width: 44, height: 44, bgcolor: 'primary.main', fontSize: 'body2.fontSize', fontWeight: 700 }}>
              {currentUser.initials}
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {t('welcomeBack', { name: currentUser.name.split(' ')[0] ?? '' })}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {role} &middot; {moduleLabel} &middot; {currentEvent.name} &middot; {currentEvent.programCode}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} spacing={3} alignItems="center">
            <BannerStat value={metrics.openCases} label={t('assigned')} />
            <BannerStat value={metrics.followUpsDueToday} label={t('dueToday')} tone="error" />
            <BannerStat value={metrics.referralsAwaitingOutcome} label={t('awaiting')} tone="warning" />
          </Stack>
        </Stack>
      </Card>

      {/* ── Action Queue (top priority — every role) ── */}
      <SectionCard
        title={`Action Queue (${actionQueue.length})`}
        action={actionQueue.length > 0 ? <Chip label={`${actionQueue.filter((a) => a.severity === 'error').length} urgent`} size="small" color="error" variant="outlined" /> : undefined}
        disableContentPadding
      >
        {actionQueue.length > 0 ? (
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '4%' }} />
                  <TableCell align="center">Client</TableCell>
                  <TableCell align="center">Case</TableCell>
                  <TableCell align="center">Action Needed</TableCell>
                  <TableCell align="center">Details</TableCell>
                  <TableCell align="right" sx={{ width: '8%' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {actionQueue.slice(0, 15).map((item) => (
                  <TableRow key={item.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigateToPath(`cases/${item.caseId}`)}>
                    <TableCell align="center">
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.severity === 'error' ? 'error.main' : item.severity === 'warning' ? 'warning.main' : 'info.main' }} />
                    </TableCell>
                    <TableCell align="center"><Typography variant="body2" sx={{ fontWeight: 600 }}>{item.clientName}</Typography></TableCell>
                    <TableCell align="center"><Typography variant="caption">{item.caseId}</Typography></TableCell>
                    <TableCell align="center">{item.label}</TableCell>
                    <TableCell align="center"><Typography variant="caption" sx={{ color: 'text.secondary' }}>{item.detail}</Typography></TableCell>
                    <TableCell align="right" onClick={(e) => { e.stopPropagation(); navigateToPath(`cases/${item.caseId}?tab=${item.tab}`); }}><ArrowRight /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CheckCircle />
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>All caught up — no urgent actions.</Typography>
          </Box>
        )}
      </SectionCard>

      {/* ── Metrics Row ── */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard center title={t('openCases')} value={String(metrics.openCases)} subtitle={t('activeCaseFiles')} onClick={() => navigateToPath('clients/search?type=case&status=Open')} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard center title={t('followUpsDue')} value={String(metrics.followUpsDue)} subtitle={t('dueTodayCount', { n: metrics.followUpsDueToday })} onClick={() => navigateToPath('clients/search?type=case&focus=overdue-followups')} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard center title={t('recoveryGoals')} value={String(metrics.recoveryGoals)} subtitle={t('inProgressCount', { n: metrics.goalsInProgress })} onClick={() => navigateToPath('clients/search')} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard center title={t('openReferrals')} value={String(metrics.openReferrals)} subtitle={t('awaitingOutcomeCount', { n: metrics.referralsAwaitingOutcome })} onClick={() => navigateToPath('clients/search?type=case&focus=pending-referrals')} />
        </Grid>
      </Grid>

      {/* ── Supervisor: Team Workload ── */}
      {isSupervisor && caseloadData.length > 0 && (
        <SectionCard title="Team Workload" disableContentPadding>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">Case Manager</TableCell>
                  <TableCell align="center">Open</TableCell>
                  <TableCell align="center">Closed</TableCell>
                  <TableCell align="center">Overdue</TableCell>
                  <TableCell align="center">Highest Tier</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {caseloadData.map((row) => {
                  const highestTier = row.cases
                    .filter((c) => c.status === 'Open')
                    .reduce((best, c) => {
                      const num = Number(c.tier.replace(/\D/g, ''));
                      return num > best ? num : best;
                    }, 0);
                  return (
                    <TableRow key={row.name} hover sx={{ cursor: 'pointer' }} onClick={() => navigateToPath('admin?tab=caseload')}>
                      <TableCell align="center"><Typography variant="body2" sx={{ fontWeight: 600 }}>{row.name}</Typography></TableCell>
                      <TableCell align="center">
                        <Chip label={row.open} size="small" color={row.open > 5 ? 'warning' : 'default'} />
                      </TableCell>
                      <TableCell align="center">{row.closed}</TableCell>
                      <TableCell align="center">
                        {row.overdue > 0 ? <Chip label={row.overdue} size="small" color="error" /> : '0'}
                      </TableCell>
                      <TableCell align="center">{highestTier > 0 ? <TierBadge tier={`Tier ${highestTier}`} /> : '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>
      )}

      {/* ── Org Manager / Program Manager: Organization Performance ── */}
      {orgWorkloads.length > 0 && (
        <SectionCard title="Organization Performance" disableContentPadding>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">Organization</TableCell>
                  <TableCell align="center">Open Cases</TableCell>
                  <TableCell align="center">Tier 3/4</TableCell>
                  <TableCell align="center">Follow-Ups Due</TableCell>
                  <TableCell align="center">Open Referrals</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orgWorkloads.map((org) => (
                  <TableRow key={org.organization} hover>
                    <TableCell align="center"><Typography variant="body2" sx={{ fontWeight: 600 }}>{org.organization}</Typography></TableCell>
                    <TableCell align="center">{org.openCases}</TableCell>
                    <TableCell align="center">
                      {org.tier34Cases > 0 ? <Chip label={org.tier34Cases} size="small" color="warning" /> : '0'}
                    </TableCell>
                    <TableCell align="center">
                      {org.followUpsDue > 0 ? <Chip label={org.followUpsDue} size="small" color="error" /> : '0'}
                    </TableCell>
                    <TableCell align="center">{org.openReferrals}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>
      )}

      {/* ── Status & Tier (all roles) ── */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard title={t('caseStatusBreakdown')}>
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {statusBreakdown.map((s) => (
                <Grid key={s.label} size={{ xs: 6, sm: 3 }}>
                  <StatusBlock label={tv(s.label)} count={s.count} color={s.color} onClick={() => navigateToPath(`clients/search?type=case&status=${s.label}`)} />
                </Grid>
              ))}
            </Grid>
            <StackedStatusBar segments={statusBreakdown} />
          </SectionCard>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <SectionCard title={t('tierDistribution')}>
            <Stack spacing={1.5}>
              {tierDistribution.map((row) => (
                <TierBar key={row.tier} tier={tv(row.tier)} count={row.count} total={row.total} description={tv(row.description)} color={row.color} accent={row.accent ?? false} />
              ))}
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>

      {/* ── Bottom: Activity · Follow-Ups · Attention ── */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <SectionCard title={t('recentActivity')} action={<ViewAll label={t('viewAll')} onClick={() => navigateToPath('clients/search')} />}>
            <Stack>
              {recentActivity.map((item) => (
                <DenseRow
                  key={item.id}
                  leading={<IconBadge tone="primary">{item.initials}</IconBadge>}
                  primary={item.primary}
                  secondary={tv(item.secondary)}
                  trailing={<Typography variant="caption" sx={{ color: 'text.secondary' }}>{item.timestamp}</Typography>}
                  onClick={() => navigateToPath(`cases/${item.caseId}`)}
                />
              ))}
            </Stack>
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <SectionCard
            title={t('upcomingFollowUps')}
            action={<Chip label={t('dueCount', { n: metrics.followUpsDue })} size="small" color="warning" variant="outlined" />}
          >
            <Stack>
              {buildFollowUpRows().map((item) => (
                <DenseRow
                  key={item.id}
                  leading={<IconBadge tone={item.overdue ? 'error' : 'warning'}>{item.overdue ? <AlertIcon /> : <Calendar />}</IconBadge>}
                  primary={item.clientName}
                  secondary={`${item.dueLabel} · ${item.type}`}
                  trailing={<Box sx={{ display: 'flex', color: 'text.secondary', '& svg': { width: 16, height: 16 } }}><ArrowRight /></Box>}
                  onClick={() => navigateToPath(`cases/${item.caseId}`)}
                />
              ))}
            </Stack>
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <SectionCard title={t('needsAttention')} action={<Chip label={t('itemsCount', { n: needsAttention.length })} size="small" color="error" variant="outlined" />}>
            <Stack>
              {needsAttention.map((item) => {
                const Icon = ATTENTION_ICONS[item.severity];
                return (
                  <DenseRow
                    key={item.id}
                    alignTop
                    leading={<IconBadge tone={item.severity as RowTone}><Icon /></IconBadge>}
                    primary={tv(item.primary)}
                    secondary={tv(item.reason)}
                    onClick={() => navigateToPath(ATTENTION_LINKS[item.severity])}
                  />
                );
              })}
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}

/* ── Helpers ─────────────────────────────────────────────────────────── */

interface FollowUpRow { id: string; caseId: string; clientName: string; type: string; dueLabel: string; overdue: boolean; }

function buildFollowUpRows(): FollowUpRow[] {
  const caseById = new Map(mockGetCases().map((c) => [c.caseId, c] as const));
  const today = startOfToday();
  return mockGetAllFollowUps()
    .map((fu) => {
      const date = fu.nextFollowUpDate;
      const overdue = Boolean(date) && parseDateOnly(date as string) < today;
      return {
        id: fu.id,
        caseId: fu.caseId,
        clientName: caseById.get(fu.caseId)?.clientName ?? fu.caseId,
        type: fu.followUpType ?? 'Follow-up',
        dueLabel: overdue ? 'Overdue' : date ? formatDueDate(date) : 'Unscheduled',
        overdue,
      };
    })
    .sort((a, b) => Number(b.overdue) - Number(a.overdue));
}

function BannerStat({ value, label, tone = 'primary' }: { readonly value: number; readonly label: string; readonly tone?: 'primary' | 'error' | 'warning' }) {
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: (th) => tone === 'primary' ? th.palette.text.primary : th.palette[tone].main }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>{label}</Typography>
    </Box>
  );
}

function ViewAll({ label, onClick }: { readonly label: string; readonly onClick: () => void }) {
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5, border: 'none', background: 'none',
        cursor: 'pointer', p: 0, fontWeight: 700, fontSize: 'caption.fontSize', color: 'primary.main',
        '& svg': { width: 14, height: 14 }, '&:hover': { textDecoration: 'underline' },
      }}
    >
      {label}
      <ArrowRight />
    </Box>
  );
}
